import { flushSync } from 'react-dom'
import { MailSendStatus } from 'components/mail/MailSendStatus'
import { validateMailReturnTo } from 'utils/mailComposeNavigation'
import { serializeMailNavigation } from 'utils/mailNavigation'
import { MailDraftRecovery } from 'components/mail/MailDraftRecovery'
import { emptyRecipientEditor, splitMailRecipients, validMailAddress } from 'components/mail/MailRecipientInput'
import { MailHeader } from 'components/mail/mail-header'
import { useEffect, useState } from 'react'
import { MailCompositionProvider, useMailComposition } from 'contexts/MailCompositionContext'
import { AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from 'components/ui/alert-dialog'
import { MailComposer } from 'components/mail/MailComposer'
import { Link, Navigate, useSearchParams, useNavigate } from 'react-router-dom'
import { AppShell } from 'components/dashboard/app-shell'
import { useUser } from 'contexts/UserContext'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { listDocumentNodes, listDocumentSpaces } from 'services/documents'
import type { DocumentNode, DocumentSpace } from 'types/documents'
import { forwardBody, replySubject, sourceWarning } from 'utils/mailComposition'
import {
  MailApiError, MailAttachment, MailDraft, MailSend, Mailbox, DraftContent, DraftMode,
  addressValue, confirmMailDraft, getMailDraft, getMailMessage, getMailSend,
  listMailboxes, listMailDrafts, resolveMailSend,
} from 'services/mail'

const editableContent = ({ to, cc, bcc, subject, body_text, document_ids, attachment_ids, reply_to, mode }: DraftContent): DraftContent => ({ to, cc, bcc, subject, body_text, document_ids, attachment_ids, reply_to, mode })
const blank: DraftContent = { to: [], cc: [], bcc: [], subject: '', body_text: '', document_ids: [], attachment_ids: [], reply_to: null, mode: 'new' }
const split = splitMailRecipients
const errorText = (error: unknown) => {
  const e = error as MailApiError
  if (e.status === 409) return 'Conflitto: bozza modificata, fonte non disponibile o conferma già registrata. Ricarica la bozza.'
  if (e.status === 403 || e.status === 404) return 'Accesso revocato o risorsa non disponibile.'
  return e.message || 'Operazione non riuscita.'
}

export default function MailComposePage() {
  const session = useMailComposition()
  return session ? <MailCompositionEditor /> : <MailCompositionProvider><MailCompositionEditor /></MailCompositionProvider>
}

export function MailCompositionEditor({ inline = false, onCloseInline, onSourceUnavailable }: { inline?: boolean; onCloseInline?: () => void; onSourceUnavailable?: (sourceId: string) => void }) {
  const { user, loading: userLoading } = useUser()
  const [routeParams, setRouteParams] = useSearchParams()
  const navigate = useNavigate()
  const { internalNavigationTarget, resolutionPendingRead, setResolutionPendingRead, sourceState, setSourceState, resetComposition, inlineTarget, setInlineTarget, compositionParams, setCompositionParams, returnTo, setReturnTo, uploads, retryAttachmentDocuments, attachmentDocuments, autosave, recipientEditors, setRecipientEditors, showCc, setShowCc, showBcc, setShowBcc, boxes, setBoxes, drafts, setDrafts, boxId, setBoxId, draft, setDraft, content, setContent, to, setTo, cc, setCc, bcc, setBcc, sourceAttachments, setSourceAttachments, sourceNotice, setSourceNotice, spaces, setSpaces, spaceId, setSpaceId, documents, setDocuments, send, setSend, busy, setBusy, dirty, setDirty, loadingContext, setLoadingContext, error, setError, notice, setNotice, resolution, setResolution, evidence, setEvidence, pending, setPending, intent, setIntent, sessionId, setSessionId, loadingContextRef, confirmation, operation, skipDraftLoad, scope, recipientInputs } = useMailComposition()!
  const params = inline ? new URLSearchParams(compositionParams) : routeParams
  const setParams = (value: URLSearchParams | Record<string, string>, options?: { replace?: boolean }) => {
    const next = value instanceof URLSearchParams ? value : new URLSearchParams(value)
    setCompositionParams(next.toString())
    if (!inline && routeParams.toString() !== next.toString()) {
      internalNavigationTarget.current = `/mail/compose${next.size ? `?${next.toString()}` : ''}`
      setRouteParams(next, options)
    }
  }
  const [requestedBox, setRequestedBox] = useState<string | null>(null)
  const resetRecipientEditors = () => { setRecipientEditors({ to: emptyRecipientEditor(), cc: emptyRecipientEditor(), bcc: emptyRecipientEditor() }); setShowCc(false); setShowBcc(false) }
  const revokeCompositionSource = (sourceId = params.get('message') || content.reply_to || draft?.reply_to || '') => { if (inline && onSourceUnavailable && sourceId) onSourceUnavailable(sourceId); else { resetComposition(); setBoxId('') } }
  const openingIntent = params.get('draft') ? `draft:${params.get('draft')}` : params.get('message') ? `${params.get('mode')}:${params.get('message')}` : `new:${params.get('mailbox') || ''}`
  useEffect(() => { if (!inline) setCompositionParams(routeParams.toString()) }, [inline, routeParams.toString()])
  const box = boxes.find(item => item.id === boxId)

  useEffect(() => {
    if (!draft || loadingContext || intent !== openingIntent || params.get('draft') === draft.id) return
    const next = new URLSearchParams({ draft: draft.id })
    const back = validateMailReturnTo(params.get('returnTo') || returnTo)
    if (back) next.set('returnTo', back)
    setIntent(`draft:${draft.id}`); skipDraftLoad.current = draft.id
    setParams(next, { replace: true })
  }, [draft?.id, loadingContext, intent, openingIntent])

  useEffect(() => {
    if (!user?.enabled_modules?.emails) return
    let active = true
    listMailboxes().then(mailboxes => {
      if (!active) return
      setBoxes(mailboxes.results)
      if (!boxId) setBoxId(params.get('mailbox') || mailboxes.results.find(item => item.permissions['mail.draft'])?.id || '')
    }).catch(err => { if (active) setError(errorText(err)) })
    return () => { active = false; scope.current++; if (loadingContextRef.current) setIntent(null) }
  }, [user?.enabled_modules?.emails])

  useEffect(() => {
    if (params.get('draft') || params.get('message') || intent === openingIntent) return
    scope.current++
    setIntent(openingIntent); setSessionId(crypto.randomUUID()); resetRecipientEditors()
    setDraft(null); setSend(null); setContent(blank); setTo(''); setCc(''); setBcc('')
    setInlineTarget(null); setReturnTo(validateMailReturnTo(params.get('returnTo') || '') || '')
    setSourceAttachments([]); setSourceNotice(''); setSourceState('none'); setDirty(false); setError(''); setNotice(''); setLoadingContext(false)
    if (params.get('mailbox')) setBoxId(params.get('mailbox')!)
  }, [openingIntent])

  useEffect(() => {
    const id = params.get('draft')
    if (!id || intent === openingIntent) return
    setIntent(openingIntent)
    confirmation.current = null
    setSessionId(crypto.randomUUID()); resetRecipientEditors()
    if (skipDraftLoad.current === id) { skipDraftLoad.current = null; return }
    const current = ++scope.current
    setLoadingContext(true); setSourceState('loading')
    setDraft(null); setSend(null); setError(''); setContent(blank); setTo(''); setCc(''); setBcc('')
    setSourceNotice(''); setSourceState('loading')
    getMailDraft(id).then(item => {
      if (scope.current !== current) return
      if (item.reply_to && validateMailReturnTo(params.get('returnTo') || '')) setInlineTarget(item.reply_to)
      setReturnTo(validateMailReturnTo(params.get('returnTo') || '') || '')
      setDraft(item); setBoxId(item.mailbox); setContent(editableContent(item))
      setTo(item.to.join(', ')); setCc(item.cc.join(', ')); setBcc(item.bcc.join(', '))
      setDirty(false)
      setSourceAttachments([])
      setSourceState(item.reply_to && item.status !== 'confirmed' ? 'loading' : 'none')
      if (item.reply_to && item.status !== 'confirmed') getMailMessage(item.reply_to).then(source => {
        if (scope.current === current) {
          setSourceAttachments(source.attachments)
          if (source.mailbox !== item.mailbox) throw new Error('Fonte di un\u2019altra casella.')
          setSourceState(source.availability === 'expired' ? 'unavailable' : 'ready')
          setSourceNotice(sourceWarning(source))
        }
      }).catch(err => { if (scope.current === current) { setSourceState('unavailable'); if ([403, 404].includes((err as MailApiError).status)) { revokeCompositionSource() }; setError(errorText(err)) } })
      setSend(null)
    }).catch(err => { if (scope.current === current) {
      setDraft(null); setSend(null); setContent(blank); setTo(''); setCc(''); setBcc('')
      setSourceAttachments([]); setError(errorText(err))
    } }).finally(() => { if (scope.current === current) setLoadingContext(false) })
  }, [openingIntent])

  useEffect(() => {
    const mode = params.get('mode') as DraftMode | null
    const message = params.get('message')
    if (!message || !mode || params.get('draft') || intent === openingIntent) return
    setIntent(openingIntent)
    confirmation.current = null
    setSessionId(crypto.randomUUID()); resetRecipientEditors()
    const current = ++scope.current
    setLoadingContext(true); setSourceState('loading')
    setDraft(null); setSend(null); setError(''); setContent(blank); setTo(''); setCc(''); setBcc(''); setSourceAttachments([])
    getMailMessage(message).then(source => {
      if (scope.current !== current) return
      if (params.get('mailbox') && params.get('mailbox') !== source.mailbox) throw new Error('La fonte non appartiene alla casella richiesta.')
      setBoxId(source.mailbox)
      setSourceState(source.availability === 'expired' ? 'unavailable' : 'ready')
      setInlineTarget(source.id); setReturnTo(validateMailReturnTo(params.get('returnTo') || '') || '')
      setContent({ ...blank, mode, reply_to: source.id,
        subject: mode === 'reply' ? replySubject(source.subject) : `Fwd: ${source.subject}`,
        attachment_ids: mode === 'forward' ? source.attachments.filter(item => item.availability === 'complete').map(item => item.id) : [], body_text: mode === 'forward' ? forwardBody(source) : '' })
      setSourceNotice(sourceWarning(source))
      setTo(mode === 'reply' ? source.sender.map(addressValue).join(', ') : '')
      setSourceAttachments(source.attachments)
      setDirty(false)
    }).catch(err => { if (scope.current === current) { setSourceState('unavailable'); if ([403, 404].includes((err as MailApiError).status)) { revokeCompositionSource() }; setError(errorText(err)) } })
      .finally(() => { if (scope.current === current) setLoadingContext(false) })
  }, [openingIntent])

  useEffect(() => {
    const id = draft?.send_id || send?.id
    if (!id || (send && !['queued', 'sending'].includes(send.status))) return
    let active = true
    const context = scope.current
    const update = () => getMailSend(id).then(item => { if (active && scope.current === context) { setSend(item); setNotice('') } })
      .catch(err => { if (active && scope.current === context) { if ([403, 404].includes((err as MailApiError).status)) { resetComposition(); setBoxId(''); setError(errorText(err)) } else setNotice('Connessione interrotta. Aggiornamento automatico dello stato in corso.') } })
    if (!send) void update()
    const timer = window.setInterval(update, 3000)
    return () => { active = false; window.clearInterval(timer) }
  }, [draft?.send_id, send?.id, send?.status])

  useEffect(() => {
    if (!pending) return
    let active = true
    let timer: ReturnType<typeof setTimeout>
    const retry = async () => {
      try {
        const result = await confirmMailDraft(pending.draft.id, pending.draft.version, pending.key)
        if (!active) return
        setSend(result)
        setDraft({ ...pending.draft, status: 'confirmed', send_id: result.id })
        setPending(null); setNotice('Invio registrato.'); setError('')
      } catch (err) {
        if (!active) return
        const status = (err as MailApiError).status
        if (status && status < 500) {
          try {
            const latest = await getMailDraft(pending.draft.id)
            if (!active) return
            setDraft(latest)
            if (latest.send_id) {
              const result = await getMailSend(latest.send_id)
              if (!active) return
              setDraft(latest); setSend(result); setPending(null); setNotice(''); setError('')
              return
            }
            setPending(null); setNotice(''); setError(errorText(err))
            return
          } catch (verification) { if (active && [403, 404].includes((verification as MailApiError).status)) { resetComposition(); setBoxId(''); setError(errorText(verification)); return } }
        }
        if (active) timer = setTimeout(retry, 3000)
      }
    }
    timer = setTimeout(retry, 3000)
    return () => { active = false; clearTimeout(timer) }
  }, [pending])

  if (userLoading) return <p role="status">Caricamento accesso…</p>
  if (!user) return <Navigate to="/login" replace />
  if (!user.enabled_modules?.emails) return <Navigate to="/dashboard-new" replace />

  const locked = intent !== openingIntent || busy || !!pending || !!autosave.recovery || loadingContext || draft?.status === 'confirmed'
  const edit = (patch: Partial<DraftContent>) => { setContent(value => ({ ...value, ...patch })); setDirty(true); setNotice('') }
  const changeBox = (id: string) => {
    if (busy || pending || autosave.saving || uploads.blocked || autosave.recovery) { setError('Attendi le operazioni in corso o completa la verifica prima di cambiare composizione.'); return }
    if (dirty || content.subject || content.body_text || to || cc || bcc || content.document_ids.length || content.attachment_ids.length) { setRequestedBox(id); return }
    resetBox(id)
  }
  const resetBox = (id: string) => {
    const next = new URLSearchParams(id ? { mailbox: id } : {})
    flushSync(() => { resetComposition(); setBoxId(id); setIntent(`new:${id}`) })
    setParams(next)
    if (inline) navigate(`/mail/compose${next.size ? `?${next}` : ''}`)
  }
  const addresses = { to: split(to), cc: split(cc), bcc: split(bcc) }
  const addressPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const valid = [...addresses.to, ...addresses.cc, ...addresses.bcc].length > 0 &&
    [...addresses.to, ...addresses.cc, ...addresses.bcc].every(item => addressPattern.test(item))
  const finalizedAddresses = { ...addresses }
  for (const name of ['to', 'cc', 'bcc'] as const) {
    const editor = recipientEditors[name]
    if (editor.buffer.trim()) {
      finalizedAddresses[name] = [...addresses[name]]
      if (editor.editing === null) finalizedAddresses[name].push(editor.buffer.trim())
      else finalizedAddresses[name].splice(editor.editing, 1, editor.buffer.trim())
    }
  }
  const payload: DraftContent = { ...content, ...finalizedAddresses }
  const lengthError = Array.from(content.subject).length > 998 ? 'L’oggetto supera il limite di 998 caratteri.' :
    Array.from(content.body_text).length > 1000000 ? 'Il testo supera il limite di 1.000.000 caratteri. Riducilo prima di salvare o inviare; il contenuto non è stato troncato.' : ''

  const save = async (sendNow = false) => {
    if (!boxId || operation.current || locked || !box || !box.permissions['mail.draft']) return
    if (sendNow && !box.permissions['mail.send']) return
    const incompleteField = (['to', 'cc', 'bcc'] as const).find(name => recipientEditors[name].buffer.length > 0 && !validMailAddress(recipientEditors[name].buffer.trim()))
    if (incompleteField) { recipientInputs.current[incompleteField]?.focus(); setError('Conferma o correggi i destinatari prima di continuare.'); return }
    if (sourceState === 'loading' || sourceState === 'unavailable') { setError('Verifica la disponibilit\u00e0 della fonte prima di salvare o inviare.'); return }
    if (sendNow && uploads.blocked) { setError('Attendi il completamento degli allegati o rimuovi i caricamenti non riusciti.'); return }
    if (sendNow && (content.document_ids.some(id => !attachmentDocuments[id]?.node || attachmentDocuments[id]?.error) || content.attachment_ids.some(id => !sourceAttachments.some(file => file.id === id && file.availability === 'complete')))) { setError('Verifica o rimuovi gli allegati non disponibili prima di inviare.'); return }
    if (lengthError) { setError(lengthError); return }
    if (![...payload.to, ...payload.cc, ...payload.bcc].length || ![...payload.to, ...payload.cc, ...payload.bcc].every(validMailAddress)) {
      const field = (['to', 'cc', 'bcc'] as const).find(name => finalizedAddresses[name].some(item => !addressPattern.test(item))) || 'to'
      recipientInputs.current[field]?.focus()
      setError('Correggi gli indirizzi dei destinatari prima di continuare.')
      return
    }
    if (content.document_ids.length + content.attachment_ids.length > 20) return
    setTo(payload.to.join(', ')); setCc(payload.cc.join(', ')); setBcc(payload.bcc.join(', '))
    setRecipientEditors({ to: emptyRecipientEditor(), cc: emptyRecipientEditor(), bcc: emptyRecipientEditor() })
    const current = scope.current
    operation.current = true
    autosave.stopTimer()
    setBusy(true); setError(''); setNotice(sendNow ? 'Invio in corso…' : '')
    try {
      const saved = await autosave.flush(payload)
      if (scope.current !== current) return
      setDraft(saved); setDirty(false)
      setDrafts(items => [saved, ...items.filter(item => item.id !== saved.id)])
      if (saved.status === 'confirmed') {
        if (saved.send_id) { const status = await getMailSend(saved.send_id); if (scope.current === current) setSend(status) }
        return
      }
      if (sendNow) {
        const tuple = confirmation.current?.draftId === saved.id && confirmation.current.version === saved.version ? confirmation.current : { draftId: saved.id, version: saved.version, key: crypto.randomUUID() }
        confirmation.current = tuple
        const key = tuple.key
        try {
          const result = await confirmMailDraft(saved.id, saved.version, key)
          if (scope.current !== current) return
          setSend(result); setDraft({ ...saved, status: 'confirmed', send_id: result.id })
          setNotice('Invio registrato.')
        } catch (err) {
          if (scope.current !== current) return
          const status = (err as MailApiError).status
          if (!status || status >= 500 || status === 409) {
            setPending({ draft: saved, key })
            setNotice('Verifica automatica dell’invio in corso…')
          } else {
            if ([403, 404].includes(status)) { resetComposition(); setBoxId(''); setError(errorText(err)) }
            else { setError(errorText(err)); setNotice('Bozza salvata. Invio non riuscito.') }
          }
        }
      } else setNotice('Bozza salvata.')
      setIntent(`draft:${saved.id}`)
      skipDraftLoad.current = saved.id
      const nextParams = new URLSearchParams({ draft: saved.id })
      const back = validateMailReturnTo(params.get('returnTo') || returnTo)
      if (back) nextParams.set('returnTo', back)
      setParams(nextParams, { replace: true })
    } catch (err) {
      if (scope.current === current) { setError(errorText(err)); setNotice('') }
    } finally {
      operation.current = false
      if (scope.current === current) setBusy(false)
    }
  }
  const resolve = async () => {
    if (!send || busy || operation.current || (!resolutionPendingRead && !evidence.trim())) return
    const current = scope.current, id = send.id
    operation.current = true; setBusy(true); setError('')
    try {
      if (resolutionPendingRead !== id) {
        await resolveMailSend(id, resolution, evidence.trim())
        if (scope.current !== current) return
        setResolutionPendingRead(id)
      }
      const latest = await getMailSend(id)
      if (scope.current !== current) return
      setSend(latest); setEvidence(''); setResolutionPendingRead(null)
    } catch (err) { if (scope.current === current) { if ([403, 404].includes((err as MailApiError).status)) { resetComposition(); setBoxId('') }; setError(errorText(err)) } }
    finally { operation.current = false; if (scope.current === current) setBusy(false) }
  }

  const title = content.mode === 'reply' ? 'Rispondi' : content.mode === 'forward' ? 'Inoltra' : 'Nuovo messaggio'
  const editor = <div className={`${inline ? 'mail-inline-composer rounded border p-3' : 'mail-compose-card'} space-y-5`}>

    {inline ? <div className="flex flex-wrap gap-2"><h3 className="font-semibold">{title}</h3><Button asChild variant="outline" disabled={busy || !!pending || loadingContext}><Link onClick={event => { if (busy || pending || loadingContext) event.preventDefault() }} to={`/mail/compose?${params.toString()}`}>Espandi</Link></Button><Button variant="ghost" onClick={onCloseInline}>Chiudi editor</Button></div> : <Button asChild variant="outline"><Link to={validateMailReturnTo(params.get('returnTo') || returnTo) || (boxId && boxes.some(item => item.id === boxId) ? serializeMailNavigation({ mailbox: boxId, seen: 'all', q: '', page: 1 }) : '/mail/inbox')}>{inlineTarget ? 'Torna alla conversazione' : 'Torna alla posta'}</Link></Button>}
    <MailDraftRecovery />
    <AlertDialog open={requestedBox !== null} onOpenChange={open => { if (!open) setRequestedBox(null) }}>
      <AlertDialogContent><AlertDialogTitle>Cambiare casella?</AlertDialogTitle>
        <AlertDialogDescription>Iniziando una nuova email il contenuto locale viene scartato. Le bozze già salvate restano disponibili nella casella originale.</AlertDialogDescription>
        <AlertDialogFooter><AlertDialogCancel>Continua questa email</AlertDialogCancel><AlertDialogAction onClick={() => { if (requestedBox !== null) resetBox(requestedBox); setRequestedBox(null) }}>Inizia una nuova email</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    {error && <p role="alert" className="text-red-700">{error}</p>}{notice && <p role="status">{notice}</p>}
    {loadingContext && <p role="status">Caricamento bozza o fonte…</p>}
    {sourceNotice && <p role="status">{sourceNotice}</p>}
    {sourceState === 'unavailable' && <p role="alert">Fonte non disponibile. Salvataggio e invio sono sospesi.</p>}
    {lengthError && <p role="alert">{lengthError}</p>}
    <div className="grid gap-3 md:grid-cols-2">
      <div><Label htmlFor="compose-mailbox">Mittente</Label><select id="compose-mailbox" className="w-full rounded border p-2" value={boxId} disabled={!!draft || busy || !!pending || autosave.saving || uploads.blocked || !!autosave.recovery} onChange={e => changeBox(e.target.value)}>
        <option value="">Seleziona casella</option>{boxes.filter(item => item.permissions['mail.draft']).map(item => <option key={item.id} value={item.id}>{item.address}</option>)}</select></div>
    </div>
    <details><summary>Dettagli composizione</summary><p>Modalità: {content.mode === 'new' ? 'Nuova mail' : content.mode === 'reply' ? 'Risposta' : 'Inoltro'}{content.reply_to && ` · Fonte ${content.reply_to}`}</p>
    {draft?.source_availability && <p>Fonte: {draft.source_availability}{draft.source_incomplete_reasons?.length ? ` · ${draft.source_incomplete_reasons.join(', ')}` : ''}</p>}
    </details>
    {(autosave.saving || uploads.blocked) && <p>Attendi il salvataggio o gestisci i caricamenti prima di cambiare mittente.</p>}
    {draft && <p>Il mittente resta bloccato dopo il primo salvataggio.</p>}
    {!loadingContext && <MailComposer sourceBlocked={sourceState === 'loading' || sourceState === 'unavailable'} pendingUploadCount={uploads.pendingCount} uploadsBlocked={uploads.blocked} retryAttachmentDocuments={retryAttachmentDocuments} attachmentDocuments={attachmentDocuments} recipientEditors={recipientEditors} setRecipientEditors={setRecipientEditors} showCc={showCc} setShowCc={setShowCc} showBcc={showBcc} setShowBcc={setShowBcc} to={to} cc={cc} bcc={bcc} setTo={setTo} setCc={setCc} setBcc={setBcc} locked={locked} split={split} recipientInputs={recipientInputs} setDirty={setDirty} valid={valid} content={content} edit={edit} draft={draft} sourceAttachments={sourceAttachments} spaceId={spaceId} setSpaceId={setSpaceId} spaces={spaces} documents={documents} box={box} save={save} changeBox={changeBox} boxId={boxId} />}
    {autosave.status && autosave.status !== error && <p role={autosave.failure ? "alert" : "status"}>{autosave.status}</p>}
    {send && <MailSendStatus send={send} mailbox={draft?.mailbox || boxId} canRead={!!box?.permissions['mail.read']} busy={busy} resolution={resolution} evidence={evidence} verificationPending={resolutionPendingRead === send.id} onResolution={setResolution} onEvidence={setEvidence} onResolve={resolve} onNew={() => changeBox(boxId)} />}
  </div>
  return inline ? <section aria-label={`${title} inline`}>{editor}</section> : <AppShell currentUser={user}><div className="mail-page mail-compose"><MailHeader title={title} description="Una nuova conversazione parte da qui." />{editor}</div></AppShell>
}
